






















































































































































































































































































































































































































































































































import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import constants from "@/constants/_message";
import { ValidationObserver } from "vee-validate";
import { isNotEmpty } from "@/utils/object";
import alert from "@/mixins/alert";
import { deleteHolidayAlert } from "@/utils/notificationContent";
import moment from "moment";
import DatePicker from "@/components/datepicker/index.vue";
import SettingProxy from "@/proxies/SettingProxy";

interface QuarterOption {
  label: string;
  display: string;
  start: string;
  end: string;
  isCurrent: boolean;
  isPast: boolean;
}

@Component({
  components: {
    DatePicker,
  },
})
class AppointmentSettingsForm extends Vue {
  public moment = moment;

  @Getter public schedulerInitData: any;
  @Getter public currentUser: any;
  @Action public getLocations: any;
  @Getter public tenantLocations: any;
  @Getter public scheduler: any;
  @Action public executeScheduler: any;
  @Action public getHolidays: any;
  @Getter public holidays: any;
  @Getter public schedulers: any;
  @Action public setScheduler: any;
  @Action public getScheduledHistory: any;
  @Action public initNewSettingsForSchduler: any;

  // Wizard state
  currentStep = 1;

  // Quarter selection
  selectedQuarter = "";
  datePickerKey = 0;
  startDateValue = "";
  endDateValue = "";

  // Location state
  locations: any = [];
  generationStarted = false;

  // Date constraints
  minStartDate = moment().toISOString();
  minEndDate = "";
  maxEndDate = "";
  endDateDisabled = true;

  // Form state
  value = 0;
  isSubmitted = false;
  genearteBtn = false;
  disableRest = false;
  localSchedulerTime: any = null; // Local state to track time for immediate validation
  localSchedulerDays: any = null; // Local state to track days for immediate display
  localStartDate: any = null; // Local state to track start date
  localEndDate: any = null; // Local state to track end date

  // History state
  historyLoading = false;
  historyLoadingMore = false;
  historyHasMore = true;
  historyLimit = 20;
  historySkip = 0;
  historySchedulers: any[] = [];

  locationFilter: any = {
    fields: ["id", "name"],
    where: { status: { eq: true }, suspend_appointments: {eq: false} },
    order: ["name ASC"],
  };

  holidayFilter: any = {
    limit: 20,
    skip: 0,
    where: {
      and: [
        {
          status: { eq: true },
          or: [
            { is_recurring: true },
            { holiday_date: { gt: moment().toISOString() } },
          ],
        },
      ],
    },
    order: "holiday_date ASC",
  };

  scheduleFilter: any = {
    where: {},
    order: "created_at DESC",
    limit: 20,
    skip: 0,
  };

  $refs!: {
    generateBulkMeeting: InstanceType<typeof ValidationObserver>;
  };

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  beforeDestroy() {
    this.setScheduler(this.schedulerInitData);
  }

  beforeMount() {
    this.initNewSettingsForSchduler();
    this.getLocations(this.locationFilter);
    this.getHolidays(this.holidayFilter);
    // Load history inline on mount
    this.loadHistory(true);
  }

  mounted() {
    // Initialize local state from Vuex getters
    this.localSchedulerTime = this.scheduler.time || null;
    this.localSchedulerDays = this.scheduler.days !== undefined ? this.scheduler.days : null;
    this.localStartDate = this.scheduler.startDate || null;
    this.localEndDate = this.scheduler.endDate || null;

    // Ensure scheduler release time defaults to 9:00 AM local time.
    // API expects a datetime string with local offset, e.g. 2026-03-17T09:00:00.000-04:00
    if (!this.localSchedulerTime) {
      const baseDate = this.localStartDate || moment().format("YYYY-MM-DD");
      const defaultTime = this.getDefaultReleaseTime(baseDate);
      this.setSchedulerTime(defaultTime);
    }
    
    // Pre-select current quarter only if no dates are set yet.
    // This keeps the initial page usable, while still allowing users to fully
    // customize start/end dates without the UI overriding their selections.
    const hasDates = !!(this.localStartDate && this.localEndDate);
    if (!hasDates) {
      const current = this.quarterOptions.find((q) => q.isCurrent);
      if (current) this.selectQuarter(current);
    }
  }

  // ─── History ─────────────────────────────────────────────────────────────────

  async loadHistory(reset = true) {
    if (reset) {
      this.historyLoading = true;
      this.historyHasMore = true;
      this.historySkip = 0;
      this.historySchedulers = [];
    } else {
      if (this.historyLoadingMore || !this.historyHasMore) return;
      this.historyLoadingMore = true;
    }

    try {
      const filter = {
        ...this.scheduleFilter,
        where: {
          ...(this.scheduleFilter.where || {}),
          tenantId: this.currentUser.tenantId,
        },
        limit: this.historyLimit,
        skip: this.historySkip,
      };

      const response = await new SettingProxy().getScheduledHistory(filter);
      const items = Array.isArray(response) ? response : [];

      this.historySchedulers = reset ? items : [...this.historySchedulers, ...items];

      if (items.length < this.historyLimit) {
        this.historyHasMore = false;
      } else {
        this.historySkip += this.historyLimit;
      }
    } catch (e) {
      this.historyHasMore = false;
    } finally {
      this.historyLoading = false;
      this.historyLoadingMore = false;
    }
  }

  refreshHistory() {
    this.loadHistory(true);
  }

  onHistoryScroll(e: Event) {
    if (this.historyLoading || this.historyLoadingMore || !this.historyHasMore) return;
    const el = e.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom) this.loadHistory(false);
  }

  calcDuration(start: string, end: string): string {
    const days = moment(end).diff(moment(start), "days") + 1;
    if (days < 90) return `${days} days`;
    const months = Math.round(days / 30);
    return `~${months} months`;
  }

  // ─── Quarter helpers ────────────────────────────────────────────────────────

  get quarterOptions(): QuarterOption[] {
    const now = moment();
    const year = now.year();
    const currentQ = Math.ceil((now.month() + 1) / 3);

    const make = (y: number, q: number): QuarterOption => {
      const starts = [
        { m: 0, d: 1 },
        { m: 3, d: 1 },
        { m: 6, d: 1 },
        { m: 9, d: 1 },
      ];
      const ends = [
        { m: 2, d: 31 },
        { m: 5, d: 30 },
        { m: 8, d: 30 },
        { m: 11, d: 31 },
      ];
      const labels = ["Jan – Mar", "Apr – Jun", "Jul – Sep", "Oct – Dec"];
      const start = moment({ year: y, month: starts[q - 1].m, day: starts[q - 1].d });
      const end   = moment({ year: y, month: ends[q - 1].m,   day: ends[q - 1].d });
      return {
        label: `Q${q} ${y}`,
        display: labels[q - 1],
        start: start.format("YYYY-MM-DD"),
        end:   end.format("YYYY-MM-DD"),
        isCurrent: y === year && q === currentQ,
        isPast: start.isBefore(now, "quarter"),
      };
    };

    const quarters: QuarterOption[] = [];
    let q = currentQ;
    let y = year;
    for (let i = 0; i < 4; i++) {
      quarters.push(make(y, q));
      q++;
      if (q > 4) { q = 1; y++; }
    }
    return quarters;
  }

  selectQuarter(q: QuarterOption) {
    this.selectedQuarter = q.label;
    
    // If selecting the current quarter, start from today instead of quarter start
    // Otherwise, use the quarter's start date
    let startDate = q.isCurrent 
      ? moment().format("YYYY-MM-DD")
      : q.start;
    
    // If weekends are disabled and start date is a weekend, move to next weekday
    if (q.isCurrent && this.currentUser.tenant && !this.currentUser.tenant.allow_weekend_meetings) {
      const startMoment = moment(startDate);
      const dayOfWeek = startMoment.day(); // 0 = Sunday, 6 = Saturday
      if (dayOfWeek === 0) {
        // If Sunday, move to Monday
        startDate = startMoment.add(1, 'day').format("YYYY-MM-DD");
      } else if (dayOfWeek === 6) {
        // If Saturday, move to Monday
        startDate = startMoment.add(2, 'days').format("YYYY-MM-DD");
      }
    }
    
    this.startDateValue = startDate;
    this.endDateValue   = q.end;
    this.datePickerKey++;
    this.setStartDate(startDate);
    this.setEndDate(q.end);
  }

  // ─── Period helpers ─────────────────────────────────────────────────────────

  get periodDays(): number {
    const startDate = this.localStartDate || this.scheduler.startDate;
    const endDate = this.localEndDate || this.scheduler.endDate;
    if (!startDate || !endDate) return 0;
    return moment(endDate).diff(moment(startDate), "days") + 1;
  }

  formatDisplayDate(val: any): string {
    if (!val) return "—";
    return moment(val).format("MMM DD, YYYY");
  }

  // ─── Status helpers ──────────────────────────────────────────────────────────

  getStatusClass(task: string): string {
    switch (task) {
      case "Completed":     return "status--success";
      case "Failed":        return "status--error";
      case "Rejected":      return "status--error";
      case "Initialize":    return "status--info";
      case "NotYetStarted": return "status--neutral";
      default:              return "status--neutral";
    }
  }

  formatStatus(task: string): string {
    if (!task || task === "NotYetStarted") return "Not Started";
    return task;
  }

  get completedCount(): number {
    return this.locations.filter((l: any) => l.task === "Completed").length;
  }

  get failedCount(): number {
    return this.locations.filter((l: any) => l.task === "Failed" || l.task === "Rejected").length;
  }

  get pendingCount(): number {
    return this.locations.filter(
      (l: any) => !l.task || l.task === "NotYetStarted" || l.task === "Initialize"
    ).length;
  }

  get generationProgress(): number {
    if (!this.locations.length) return 0;
    const done = this.locations.filter(
      (l: any) => l.task === "Completed" || l.task === "Failed" || l.task === "Rejected"
    ).length;
    return Math.round((done / this.locations.length) * 100);
  }

  // ─── Date setters ────────────────────────────────────────────────────────────

  setStartDate(value: any) {
    const prevStartDateValue = this.startDateValue;
    this.startDateValue = value;
    this.localStartDate = value; // Update local state immediately
    this.setScheduler({ ...this.scheduler, startDate: value });
    // For custom ranges, end date must be >= start date.
    this.minEndDate = moment(value).toISOString();
    // No max end-date restriction; allow fully custom ranges.
    this.maxEndDate = "";
    this.endDateDisabled = false;
    if (value !== prevStartDateValue) this.selectedQuarter = "";

    // If current end date is empty or earlier than start, snap to end-of-quarter.
    const currentEnd = this.localEndDate || this.scheduler.endDate || this.endDateValue;
    if (!currentEnd || moment(currentEnd).isBefore(moment(value), "day")) {
      const defaultEnd = moment(value).endOf("quarter").format("YYYY-MM-DD");
      this.endDateValue = defaultEnd;
      this.datePickerKey++;
      this.setEndDate(defaultEnd);
    }

    // Keep release time pinned to 9:00 AM local time on the selected start date.
    const defaultTime = this.getDefaultReleaseTime(value);
    this.setSchedulerTime(defaultTime);
  }

  setEndDate(value: any) {
    const prevEndDateValue = this.endDateValue;
    this.endDateValue = value;
    this.localEndDate = value; // Update local state immediately
    this.setScheduler({ ...this.scheduler, endDate: value });

    // If user changes the end date manually (not via quarter quick-select), clear quarter highlight.
    if (value !== prevEndDateValue && this.selectedQuarter) {
      this.selectedQuarter = "";
    }
  }

  setSchedulerDays(value: any) {
    this.localSchedulerDays = value; // Update local state immediately
    this.setScheduler({ ...this.scheduler, days: value });
  }

  setSchedulerTime(value: any) {
    this.localSchedulerTime = value; // Update local state immediately
    this.setScheduler({ ...this.scheduler, time: value });
  }

  // ─── Computed properties ──────────────────────────────────────────────────────

  getDefaultReleaseTime(baseDate: any): string {
    const date = baseDate ? moment(baseDate) : moment();
    return date
      .hour(9)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toISOString(true);
  }

  get dApptNotification(): boolean {
    return this.scheduler.dApptNotification || false;
  }

  set dApptNotification(value: boolean) {
    this.setScheduler({ ...this.scheduler, dApptNotification: value });
  }

  // ─── Form submit (step 1 → step 2) ──────────────────────────────────────────

  onFormSubmit(): void {
    this.isSubmitted = true;
    this.$refs.generateBulkMeeting.validate().then((success: any) => {
      if (!success) {
        alert.methods.appToaster({ message: constants.FORM_ERROR, type: "ERROR" });
        const firstErrorFocused: HTMLFormElement = Object(document.querySelector(".error:first-of-type"));
        if (isNotEmpty(firstErrorFocused) && !firstErrorFocused.classList.contains("IZ-select")) {
          firstErrorFocused.focus();
        }
        const errors = Object.entries(this.$refs.generateBulkMeeting.errors)
          .map(([key, value]) => ({ key, value }))
          .filter((error) => error["value"].length);
        if (errors.length) {
          this.$refs.generateBulkMeeting.refs[errors[0]["key"]].$el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }

      // Validate start date (check all possible sources)
      const startDateValue = this.localStartDate || this.scheduler.startDate || this.startDateValue;
      if (!startDateValue || startDateValue === "") {
        alert.methods.appToaster({ message: "Please select a start date", type: "ERROR" });
        return;
      }

      // Validate end date (check all possible sources)
      const endDateValue = this.localEndDate || this.scheduler.endDate || this.endDateValue;
      if (!endDateValue || endDateValue === "") {
        alert.methods.appToaster({ message: "Please select an end date", type: "ERROR" });
        return;
      }

      // Ensure time always exists (defaults to 9:00 AM local time)
      const timeValue = this.localSchedulerTime || this.scheduler.time || this.getDefaultReleaseTime(startDateValue);
      if (!this.localSchedulerTime || !this.scheduler.time) {
        this.setSchedulerTime(timeValue);
      }

      this.setScheduler({ ...this.scheduler, name: "manual" });

      if (this.tenantLocations.length) {
        this.locations = this.tenantLocations.map((v: any) => ({
          ...v, task: "NotYetStarted", reason: "", remove: false, lastAction: null,
        }));
      }
      this.value = 0;
      this.genearteBtn = false;
      this.generationStarted = false;
      this.currentStep = 2;
    });
  }

  // ─── Generation ──────────────────────────────────────────────────────────────

  generateScheduler() {
    if (this.locations.length > 0) {
      const locationIds = this.locations.map((v: any) => v.id) ?? [];
      
      // Use local state values (immediate) with fallback to Vuex getters
      const schedulerData = {
        ...this.scheduler,
        name: "manual", // Required by API
        startDate: this.localStartDate || this.scheduler.startDate,
        endDate: this.localEndDate || this.scheduler.endDate,
        days: this.localSchedulerDays !== null ? this.localSchedulerDays : (this.scheduler.days || 0),
        time: this.localSchedulerTime || this.scheduler.time,
        locationIds,
      };
      
      // Update Vuex store with complete data
      this.setScheduler(schedulerData);
      
      this.locations = this.locations.map((v: any) => ({ ...v, task: "Initialize", remove: false }));
      this.genearteBtn = true;
      this.disableRest = true;
      this.generationStarted = true;
      this.executeScheduler({ data: schedulerData, callback: this.validateSchedulerResponse });
    }
  }

  validateSchedulerResponse() {
    if (this.schedulers.length) {
      this.locations = this.schedulers.map((v: any) => {
        const index = this.locations.findIndex((a: any) => a.id == v.id);
        this.locations[index].task       = v.task;
        this.locations[index].lastAction = v.action_date;
        this.locations[index].reason     = v.reason;
        this.locations[index].remove     = false;
        return this.locations[index];
      });
    }
    // Refresh history after generation completes
    this.loadHistory(true);
  }

  resetLocations() {
    this.locations = this.tenantLocations.map((a: any) => ({
      ...a, task: "NotYetStarted", reason: "", remove: false, lastAction: null,
    }));
    this.value = 0;
    this.genearteBtn = false;
    this.generationStarted = false;
  }

  removeLocation(id: number) {
    this.locations = this.locations.filter((v: any) => v.id != id);
  }

  // ─── Holidays (split into 2 columns) ─────────────────────────────────────────
  get displayedHolidays(): any[] {
    return Array.isArray(this.holidays) ? this.holidays.slice(0, 8) : [];
  }

  get holidayLeft(): any[] {
    const items = this.displayedHolidays;
    const half = Math.ceil(items.length / 2);
    return items.slice(0, half);
  }

  get holidayRight(): any[] {
    const items = this.displayedHolidays;
    const half = Math.ceil(items.length / 2);
    return items.slice(half);
  }

  done() {
    this.initNewSettingsForSchduler();
    this.resetLocations();
    this.currentStep = 1;
    this.selectedQuarter = "";
    this.startDateValue  = "";
    this.endDateValue    = "";
    // Reset all local state
    this.localSchedulerTime = this.getDefaultReleaseTime(moment().format("YYYY-MM-DD"));
    this.localSchedulerDays = null;
    this.localStartDate = null;
    this.localEndDate = null;
    this.setSchedulerTime(this.localSchedulerTime);
    this.datePickerKey++;
    this.loadHistory(true);
  }

  // ─── Navigation ─────────────────────────────────────────────────────────────

  onCancel() {
    const message = "Are you sure you want to cancel?";
    alert.methods.conFirmAlert(
      { ...deleteHolidayAlert, message },
      () => { this.backToSettings(); },
      () => {}
    );
  }

  backToSettings(): void {
    this.setScheduler(this.schedulerInitData);
    this.$router.push({ name: "setting.list" }).catch();
  }

  addHoliday() {
    this.$router.push({ name: "holidays.index" }).catch();
  }
}

export default AppointmentSettingsForm;
